import { logger } from "src/core/globals";
import { Message } from "src/gen/schema/syncfollow/syncfollow";
import { updateActiveApiOriginFromFetchResponse } from "src/utils/activeApiEndpoint";

import { Observable } from "./observable";

type EVENT = "reconnect" | "connect" | "open" | "message" | "error" | "close";
const HEART_BEAT = 30000;
const MAX_COOLDOWN = 10000;
/**
 * After this many consecutive WebSocket failures, fire a lightweight REST
 * probe to re-discover the correct API origin. Set to 1 so recovery from
 * a cell-pair switchover or wrong-region login happens on the first retry.
 */
const RECONNECT_PROBE_THRESHOLD = 1;
export const KEEP_ALIVE_MSG = "Keep Alive";
const KEEP_ALIVE_TYPE = "echoResponse";

const isRestKeepAlive = (msg: string | ArrayBuffer): boolean => {
  if (typeof msg === "string") {
    return msg.includes(KEEP_ALIVE_TYPE) && msg.includes(KEEP_ALIVE_MSG);
  }
  // For binary messages, pass along so that we only have to unmarshal once!
  return false;
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export class Socket extends Observable<any> {
  static RECONNECT: EVENT = "reconnect";
  static CONNECT: EVENT = "connect";
  static OPEN: EVENT = "open";
  static MESSAGE: EVENT = "message";
  static ERROR: EVENT = "error";
  static CLOSE: EVENT = "close";

  private keepAliveTimerId: NodeJS.Timeout | null = null;
  private pingTimeoutId: NodeJS.Timeout | null = null;
  private transportType: "json" | "binary" = "json";

  public reconnect: boolean = true;
  public connected: boolean = false;
  /* Private instance variables */
  private socket: WebSocket | undefined;
  /**
   * queue will buffer messages if the
   * websocket is not connected. These
   * messages will be sent when the
   * websocket is connected.
   */
  private queue: Array<string | Uint8Array> = [];
  private cooldown = 500;
  private consecutiveFailures = 0;

  constructor() {
    super();
    this.addEvents([Socket.ERROR, Socket.OPEN, Socket.MESSAGE, Socket.CLOSE]);
  }

  /**
   * The init method initializes the WebSocket connection.
   * It checks the current state of the WebSocket and, if
   * it is not already connecting or open, creates a new
   * WebSocket instance.
   *
   * Accepts a URL string or a builder function.  A builder
   * is re-invoked on every reconnect so the resolved host
   * can change at runtime (e.g. after a REST redirect
   * updates `window.cnc.activeApiOrigin`).
   */
  init(url: string | (() => string)) {
    const resolved = typeof url === "string" ? url : url();

    this.transportType = window.cnc.settings.get("useBinarySyncFollow")
      ? "binary"
      : "json";

    try {
      const readyState = this.socket?.readyState;
      switch (readyState) {
        /**
         * There exists a race condition
         * where if we try to send a flurry
         * of requests the socket will
         * open again and again while it
         * is connecting. If we are already
         * connecting just return and pass the
         * request. it will be queued and sent
         * when the socket is open.
         *
         * Also, a small chance it
         * opened while this was sent.
         */
        case WebSocket.CONNECTING:
        case WebSocket.OPEN:
          return;
        default: {
          const wsUrl = new URL(resolved);
          if (window.cnc.xCsrfToken) {
            wsUrl.searchParams.append("xCsrfToken", window.cnc.xCsrfToken);
          }
          const ws = new WebSocket(wsUrl);
          /**
           * Event handlers are set up for the WebSocket
           * to handle onopen, onmessage, onerror, and
           * onclose events.
           * For example, the onopen event
           * handler sets the connected flag to true, sends
           * any queued messages, and starts the keep-alive
           * mechanism
           */
          ws.onopen = () => {
            this.connected = true;
            this.consecutiveFailures = 0;
            this.fireEvent(Socket.OPEN, true);
            this.queue.forEach((msg: string | Uint8Array) => {
              if (msg) {
                this.send(msg);
              }
            });
            /* once the messages have been sent reset the queue */
            this.queue = [];
            this.cooldown = 500;
            this.keepAlive();
          };
          if (this.transportType === "binary") {
            ws.binaryType = "arraybuffer";
          }
          ws.onmessage = (m: MessageEvent<string | ArrayBuffer>) => {
            /**
             * If we get any message on the socket
             * after we sent the ping we can clear
             * the ping timeout. This is just to
             * be sure if we send a ping and get
             * no response we close the socket
             * and reconnect.
             */
            if (this.pingTimeoutId) {
              clearTimeout(this.pingTimeoutId);
            }
            if (!isRestKeepAlive(m.data)) {
              // for binary messages, pass along, as we cannot inspect payload without unmarshalling
              // that logic is handled in the syncFollowStreamManager.handleMessage
              this.fireEvent(Socket.MESSAGE, m);
            }
          };
          ws.onerror = (e: Event) => {
            this.fireEvent(Socket.ERROR, e);
            logger.error(e);
          };
          ws.onclose = (e: CloseEvent) => {
            this.consecutiveFailures++;
            if (this.reconnect) {
              if (this.consecutiveFailures >= RECONNECT_PROBE_THRESHOLD) {
                // After a disconnect the active cell may have changed.
                // A lightweight REST probe re-discovers the correct origin
                // so the next reconnect targets the right host.
                fetch("/ui/v1/sessions", {
                  method: "GET",
                  credentials: "include",
                  redirect: "follow"
                })
                  .then((r) => updateActiveApiOriginFromFetchResponse(r))
                  .catch(() => {
                    /* probe is best-effort */
                  });
              }
              setTimeout(() => {
                this.init(url);
                this.fireEvent(Socket.RECONNECT, true);
                this.cooldown = Math.min(this.cooldown * 2, MAX_COOLDOWN);
              }, this.cooldown);
            }
            this.connected = false;
            this.queue.length = 0;
            this.fireEvent(Socket.CLOSE, e);
          };

          this.socket = ws;
        }
      }
    } catch (e: unknown) {
      logger.error(e);
    }
  }

  close() {
    /* clean up the keep alive interval */
    if (this.keepAliveTimerId) {
      clearInterval(this.keepAliveTimerId);
    }
    /* clean up the ping timeout */
    if (this.pingTimeoutId) {
      clearTimeout(this.pingTimeoutId);
    }
    this.reconnect = false;
    this.socket?.close();
  }

  /**
   * Keeps the WebSocket connection alive by sending periodic ping messages.
   *
   * This method sets up an interval that sends a ping message
   * to the WebSocket server every 30 seconds. The interval is
   * stored in `this.keepAliveTimerId` so it can be cleared
   * later if needed.
   */
  keepAlive() {
    const socket = this.socket;
    if (this.keepAliveTimerId) {
      clearInterval(this.keepAliveTimerId);
    }
    this.keepAliveTimerId = setInterval(() => {
      if (socket) {
        const requestTs = new Date();
        // Create an Echo request
        const keepAliveMessage = Message.create({
          echoRequest: {
            numResponses: 1,
            message: KEEP_ALIVE_MSG,
            waitSecs: 0,
            requestTs
          }
        });

        if (this.transportType === "binary") {
          socket.send(Message.encode(keepAliveMessage).finish());
        } else {
          socket.send(JSON.stringify(keepAliveMessage, null, "  "));
        }

        /**
         * when we send a ping message, we
         * also set a timeout to close the
         * socket if we do not get a response
         * in 5 seconds.
         */
        if (this.pingTimeoutId) {
          clearTimeout(this.pingTimeoutId);
        }
        this.pingTimeoutId = setTimeout(() => {
          socket.close();
        }, 5000);
      }
    }, HEART_BEAT);
  }

  send(msg: string | Uint8Array) {
    if (this.connected && this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(msg);
        return;
      } catch (e: unknown) {
        // we do nothing with this.
        logger.error(e);
      }
    }
    /**
     * Since there is a chance that a
     * component might send a message
     * before the socket is open, this
     * will add queue up those messages,
     * they will be sent when the
     * socket opens.
     */
    this.queue.push(msg);
  }
}
